import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  GetAdminPostById,
  UpdatePropAdminPost,
  UpdatePostImages,
} from "../services/AdminPostService";
import avatar from "../images/logo-2.png";
import { useDispatch } from "react-redux";
import { setErrorMessage, setSuccessMessage } from "../slices/messageSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

const uri = `${import.meta.env.VITE_IMAGE_URL}`;

const DetailAdminPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    title: "",
    content: "",
    timeRelease: "",
    active: false,
    isShown: false,
  });
  const [images, setImages] = useState([]); // {link, file, preview}
  const [imagesToAdd, setImagesToAdd] = useState([]); // file
  const [imagesToRemove, setImagesToRemove] = useState([]); // link
  const [previewImg, setPreviewImg] = useState(null); // modal preview ảnh
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await GetAdminPostById(id);
        if (res.status_code == 200 && res.data) {
          setPost(res.data);
          setForm({
            id: id,
            title: res.data.title || "",
            content: res.data.content || "",
            timeRelease: res.data.timeRelease || "",
            active: res.data.active || false,
            isShown: res.data.isShown || false,
          });
          setImages(
            (res.data.imageLinks || []).map((img) => ({ link: img.link }))
          );
          setImagesToAdd([]);
          setImagesToRemove([]);
        } else {
          dispatch(setErrorMessage("Lấy thông tin bài đăng thất bại!"));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id, editMode]);

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => setEditMode(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleAddImage = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImages((prev) => [...prev, { file, preview: ev.target.result }]);
        setImagesToAdd((prev) => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = null;
  };

  const handleRemoveImage = (idx) => {
    const img = images[idx];
    setImages((prev) => prev.filter((_, i) => i !== idx));
    if (img.link) setImagesToRemove((prev) => [...prev, img.link]);
    if (img.file) setImagesToAdd((prev) => prev.filter((f) => f !== img.file));
  };

  const filesToBase64 = (files) => {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      })
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let formattedTimeRelease = "";
      if (form.timeRelease) {
        const date = new Date(form.timeRelease);
        if (!isNaN(date.getTime())) {
          // Format in local timezone to avoid UTC conversion
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          formattedTimeRelease = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
      }
      const res1 = await UpdatePropAdminPost(
        form.id,
        form.title,
        form.content,
        formattedTimeRelease,
        form.active ? 1 : 0,
        form.isShown ? 1 : 0
      );
      // 2. Xử lý ảnh thêm
      const addResults = [];
      if (imagesToAdd.length > 0) {
        for (const file of imagesToAdd) {
          const base64Arr = await filesToBase64([file]);
          const image64 = JSON.stringify(base64Arr);
          const res = await UpdatePostImages(id, "add", image64, "");
          addResults.push(res);
        }
      }

      // 3. Xử lý ảnh xóa
      const removeResults = [];
      for (const link of imagesToRemove) {
        const res = await UpdatePostImages(id, "remove", "", link);
        removeResults.push(res);
      }

      const allAddOk = addResults.every((r) => r?.status_code === 200);
      const allRemoveOk = removeResults.every((r) => r?.status_code === 200);
      if (res1.status_code === 200 && allAddOk && allRemoveOk) {
        setEditMode(false);
        dispatch(setSuccessMessage("Cập nhật bài đăng thành công!"));
      } else {
        dispatch(setErrorMessage("Cập nhật bài đăng thất bại!"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Drag & drop
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImages((prev) => [...prev, { file, preview: ev.target.result }]);
        setImagesToAdd((prev) => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-300">
        Đang tải dữ liệu...
      </div>
    );
  }
  if (!post) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Không tìm thấy bài viết
      </div>
    );
  }

  if (editMode) {
    return (
      <div className="flex justify-center min-h-screen bg-gray-100 dark:bg-gray-900 px-2 sm:px-4 py-6">
        <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 py-6 space-y-6">
            {/* Tiêu đề */}
            <div>
              <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">
                Tiêu đề
              </label>
              <input
                name="title"
                value={form.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Nội dung */}
            <div>
              <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">
                Nội dung
              </label>
              <textarea
                name="content"
                value={form.content}
                onChange={handleInputChange}
                rows={10}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Thời gian */}
            <div>
              <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">
                Thời gian phát hành
              </label>
              <input
                type="datetime-local"
                name="timeRelease"
                value={form.timeRelease}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Checkbox */}
            <div className="flex flex-col sm:flex-row gap-4">
              <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  name="active"
                  checked={form.active}
                  onChange={handleInputChange}
                  className="accent-blue-500"
                />
                Hoạt động
              </label>
              <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  name="isShown"
                  checked={form.isShown}
                  onChange={handleInputChange}
                  className="accent-blue-500"
                />
                Hiển thị
              </label>
            </div>

            {/* Ảnh */}
            <div>
              <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">
                Ảnh bài viết
              </label>
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-5 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 bg-gray-50 dark:bg-gray-700 transition"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => document.getElementById("fileInput").click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAddImage}
                  className="hidden"
                />
                <p className="text-gray-500 dark:text-gray-300">
                  Kéo & thả ảnh vào đây hoặc{" "}
                  <span className="text-blue-500 underline">chọn ảnh</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">Tối đa 10 ảnh.</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Đã chọn: {images.length} ảnh
                </p>
              </div>

              {/* Danh sách ảnh */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative group rounded-lg overflow-hidden shadow-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                  >
                    <img
                      src={img.preview || `${uri}/${img.link}`}
                      alt="Ảnh"
                      className="w-full h-44 object-contain transition-transform duration-200 group-hover:scale-105 cursor-pointer"
                      onClick={() =>
                        setPreviewImg(img.preview || `${uri}/${img.link}`)
                      }
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 shadow-md z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage(idx);
                      }}
                      title="Xóa ảnh"
                    >
                      <FontAwesomeIcon icon={faTrash} className="px-1"/>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal preview ảnh */}
            {previewImg && (
              <div
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
                onClick={() => setPreviewImg(null)}
              >
                <img
                  src={previewImg}
                  alt="Preview"
                  className="max-w-[90vw] max-h-[80vh] rounded-xl border-4 border-white shadow-2xl"
                />
              </div>
            )}

            {/* Nút hành động */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow"
                onClick={handleSave}
              >
                Lưu
              </button>
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-lg shadow"
                onClick={handleCancel}
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <div className="w-[1000px] bg-white dark:bg-gray-800 rounded-lg shadow-lg p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <img
              src={avatar}
              alt="Admin"
              className="w-12 h-12 rounded-full border object-cover"
            />
            <div>
              <div className="font-bold text-lg text-gray-900 dark:text-white">
                Admin
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {post.timeRelease}
              </div>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              post.isShown
                ? "bg-green-100 text-green-700"
                : "bg-gray-300 text-gray-600"
            }`}
          >
            {post.isShown ? "Hiển thị" : "Ẩn"}
          </span>
        </div>
        {/* Content */}
        <div className="px-6 pb-6 mt-4">
          {post.title && (
            <div className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-left">
              {post.title}
            </div>
          )}
          {post.content && (
            <div className="text-base text-gray-800 dark:text-gray-200 mb-4 whitespace-pre-line text-left">
              {post.content}
            </div>
          )}
          {post.imageLinks && post.imageLinks.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {post.imageLinks.map((img, idx) => (
                <img
                  key={idx}
                  src={`${uri}/${img.link}`}
                  alt="Ảnh bài viết"
                  className="w-full max-h-[400px] object-contain rounded-lg border"
                />
              ))}
            </div>
          )}
        </div>
        <div className="px-6 pb-4 flex gap-2">
          <button
            className="bg-gray-400 dark:bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-500 dark:hover:bg-gray-600"
            onClick={() => navigate(-1)}
          >
            Quay lại
          </button>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={handleEdit}
          >
            Chỉnh sửa
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailAdminPost;
